export const TELEGRAM_BOT_NAME = "BookApp2026";
export const TELEGRAM_DEFAULT_BOT_USERNAME = "bookapp2026_bot";
export const TELEGRAM_MAIN_MINI_APP_SHORT_NAME = "bookapp";
export const TELEGRAM_MENU_BUTTON_TEXT = "打开记账";
export const TELEGRAM_MINI_APP_VIEWS = ["home", "flow", "history"] as const;
export const TELEGRAM_DEFAULT_MINI_APP_VIEW: TelegramMiniAppView = "home";

export type TelegramMiniAppView = (typeof TELEGRAM_MINI_APP_VIEWS)[number];
export type TelegramMiniAppRoute = "/" | "/flow" | "/history";

export interface TelegramMiniAppTarget {
  view: TelegramMiniAppView;
}

export const TELEGRAM_MENU_BUTTON_TARGET: TelegramMiniAppTarget = {
  view: TELEGRAM_DEFAULT_MINI_APP_VIEW,
};

export const TELEGRAM_BOT_COMMANDS = [
  { command: "start", description: "打开主入口" },
  { command: "new", description: "开始新记账流程" },
  { command: "status", description: "查看当前流程与最近结果" },
  { command: "app", description: "打开 Mini App" },
  { command: "cancel", description: "取消当前流程" },
  { command: "help", description: "查看帮助" },
] as const;

export const TELEGRAM_BOT_SHORT_DESCRIPTION = "在 Telegram 里快速记账，继续流程并查看最近记录。";

export const TELEGRAM_BOT_DESCRIPTION = [
  `${TELEGRAM_BOT_NAME} 是 Telegram 内的轻量记账 Bot 与 Mini App。`,
  "支持发起新记账流程、查看当前流程状态，并在 Mini App 中继续编辑或查看历史记录。",
  "发送 /new 开始记账，/status 查看当前流程，/app 打开 Mini App，/help 查看完整帮助。",
].join("\n");

export const TELEGRAM_BOTFATHER_MANUAL_ITEMS = [
  "确认 Bot 显示名、@username 和头像已在 BotFather 配置完成。",
  "确认 Main Mini App short name 为 bookapp，入口 URL 指向正式 Mini App 域名。",
  "确认 Main Mini App 的 icon、截图、分类和审核文案已按上线版本补齐。",
  "确认隐私政策 URL、服务条款 URL 和支持联系方式已可公开访问。",
] as const;

export const TELEGRAM_RELEASE_CHECKLIST = [
  "Bot API 自动配置完成：Webhook、commands、description、short description、chat menu button。",
  "BotFather 人工项完成：用户名、头像、Main Mini App、应用图标与商店素材。",
  "正式域名、隐私政策 URL、服务条款 URL 与支持邮箱已准备好。",
  "Mini App 在 iOS、Android 和 Desktop Telegram 中完成打开与深链验证。",
] as const;

const START_PARAM_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export function buildTelegramMiniAppUrl(baseUrl: string, target: Partial<TelegramMiniAppTarget> = {}): string {
  const url = new URL(baseUrl);
  url.searchParams.set("view", normalizeMiniAppView(target.view) || TELEGRAM_DEFAULT_MINI_APP_VIEW);
  return url.toString();
}

export function buildTelegramMenuButton(
  baseUrl: string,
  target: Partial<TelegramMiniAppTarget> = TELEGRAM_MENU_BUTTON_TARGET,
  buttonText = TELEGRAM_MENU_BUTTON_TEXT,
) {
  return {
    type: "web_app" as const,
    text: buttonText,
    web_app: {
      url: buildTelegramMiniAppUrl(baseUrl, target),
    },
  };
}

export function buildTelegramBotStartLink(botUsername: string, startParam?: string): string {
  const username = normalizeBotUsername(botUsername);
  const url = new URL(`https://t.me/${username}`);
  const normalizedStartParam = normalizeMiniAppStartParam(startParam);
  if (normalizedStartParam) {
    url.searchParams.set("start", normalizedStartParam);
  }
  return url.toString();
}

export function buildTelegramMiniAppDeepLink(
  botUsername: string,
  appShortName: string,
  startParam?: string,
): string {
  const username = normalizeBotUsername(botUsername);
  const shortName = normalizeMiniAppShortName(appShortName);
  const url = new URL(`https://t.me/${username}/${shortName}`);
  const normalizedStartParam = normalizeMiniAppStartParam(startParam);
  if (normalizedStartParam) {
    url.searchParams.set("startapp", normalizedStartParam);
  }
  return url.toString();
}

export function miniAppTargetToStartParam(target: Partial<TelegramMiniAppTarget>): string {
  const view = normalizeMiniAppView(target.view) || TELEGRAM_DEFAULT_MINI_APP_VIEW;
  return view === "history" ? "history" : view;
}

export function decodeMiniAppStartParam(value: string | null | undefined): TelegramMiniAppTarget | null {
  const normalized = normalizeMiniAppStartParam(value);
  if (!normalized) {
    return null;
  }

  if (normalized === "home" || normalized === "start" || normalized === "app" || normalized === "miniapp") {
    return { view: "home" };
  }
  if (
    normalized === "flow" ||
    normalized === "new" ||
    normalized === "add" ||
    normalized === "quick_add" ||
    normalized === "quick-add"
  ) {
    return { view: "flow" };
  }
  if (
    normalized === "history" ||
    normalized === "records" ||
    normalized === "list" ||
    normalized === "status" ||
    normalized === "stats" ||
    normalized === "summary" ||
    normalized === "today" ||
    normalized === "report"
  ) {
    return { view: "history" };
  }

  return null;
}

export function resolveTelegramMiniAppTarget(search: string | URLSearchParams): TelegramMiniAppTarget {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const targetFromStartParam =
    decodeMiniAppStartParam(params.get("tgWebAppStartParam")) ||
    decodeMiniAppStartParam(params.get("startapp")) ||
    decodeMiniAppStartParam(params.get("start"));
  const view =
    normalizeMiniAppView(params.get("view")) ||
    parseLegacyEntryParam(params.get("entry")) ||
    targetFromStartParam?.view ||
    TELEGRAM_DEFAULT_MINI_APP_VIEW;

  return { view };
}

export function resolveTelegramMiniAppInitialRoute(
  pathname: string | null | undefined,
  search: string | URLSearchParams,
): TelegramMiniAppRoute {
  const directRoute = normalizeTelegramMiniAppRoute(pathname);
  if (directRoute && directRoute !== "/") {
    return directRoute;
  }
  return telegramMiniAppViewToRoute(resolveTelegramMiniAppTarget(search).view);
}

export function telegramMiniAppViewToRoute(view: TelegramMiniAppView): TelegramMiniAppRoute {
  switch (view) {
    case "flow":
      return "/flow";
    case "history":
      return "/history";
    case "home":
    default:
      return "/";
  }
}

export function telegramMiniAppRouteToView(route: string | null | undefined): TelegramMiniAppView | null {
  const normalizedRoute = normalizeTelegramMiniAppRoute(route);
  if (normalizedRoute === "/") {
    return "home";
  }
  if (normalizedRoute === "/flow") {
    return "flow";
  }
  if (normalizedRoute === "/history") {
    return "history";
  }
  return null;
}

export function normalizeMiniAppStartParam(value: string | null | undefined): string {
  const trimmed = (value || "").trim();
  return START_PARAM_PATTERN.test(trimmed) ? trimmed : "";
}

export function normalizeMiniAppView(value: string | null | undefined): TelegramMiniAppView | null {
  if (value === "home") {
    return "home";
  }
  if (value === "flow" || value === "add" || value === "new") {
    return "flow";
  }
  if (value === "history" || value === "records" || value === "stats") {
    return "history";
  }
  return null;
}

function normalizeBotUsername(value: string): string {
  const username = value.trim().replace(/^@/, "");
  if (!/^[A-Za-z0-9_]{5,32}$/.test(username)) {
    throw new Error("Invalid Telegram bot username");
  }
  return username;
}

function normalizeMiniAppShortName(value: string): string {
  const shortName = value.trim();
  if (!/^[A-Za-z0-9_]{3,30}$/.test(shortName)) {
    throw new Error("Invalid Telegram Mini App short name");
  }
  return shortName;
}

function parseLegacyEntryParam(value: string | null | undefined): TelegramMiniAppView | null {
  return normalizeMiniAppView(value);
}

function normalizeTelegramMiniAppRoute(pathname: string | null | undefined): TelegramMiniAppRoute | null {
  const normalized = (pathname || "").replace(/\/+$/u, "") || "/";
  if (normalized === "/") {
    return "/";
  }
  if (normalized === "/flow" || normalized.startsWith("/flow/")) {
    return "/flow";
  }
  if (normalized === "/history" || normalized === "/submission" || normalized.startsWith("/submission/")) {
    return "/history";
  }
  if (normalized === "/result" || normalized.startsWith("/result/")) {
    return "/history";
  }
  return null;
}
